import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, MapPin, LogIn, LogOut, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckInButton = () => {
  const { token: authToken, user } = useAuth();
  
  // Also check localStorage for token (persistent login)
  const token = authToken || localStorage.getItem('suntech_mobile_token') || localStorage.getItem('token');
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'check-in' or 'check-out'
  const [locationError, setLocationError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/attendance/my-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching attendance status:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    // Refresh status every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          let message = 'Unable to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location permission denied. Please enable location access.';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              message = 'Location request timed out.';
              break;
            default:
              message = 'An unknown error occurred.';
          }
          reject(new Error(message));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  const handleAction = async (type) => {
    setActionType(type);
    setLocationError(null);
    setCurrentLocation(null);

    try {
      const location = await getCurrentLocation();
      setCurrentLocation(location);
      setConfirmOpen(true);
    } catch (error) {
      setLocationError(error.message);
      setConfirmOpen(true);
    }
  };

  const confirmAction = async () => {
    if (!token) {
      toast.error('Session expired. Please login again.');
      return;
    }
    
    if (!currentLocation) {
      toast.error('Location is required for check-in/check-out');
      return;
    }

    setActionLoading(true);
    try {
      const endpoint = actionType === 'check-in' ? 'check-in' : 'check-out';
      const response = await fetch(
        `${API_URL}/api/attendance/${endpoint}?latitude=${currentLocation.latitude}&longitude=${currentLocation.longitude}&accuracy=${currentLocation.accuracy || 0}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        if (data.warning) {
          toast.warning(data.warning);
        }
        setConfirmOpen(false);
        fetchStatus();
      } else {
        let errorMessage = `Failed to ${actionType}`;
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Check-in/out error:', error);
      toast.error(`Network error: ${error.message || 'Please check your connection'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minutes} ${suffix}`;
  };

  if (loading) {
    return null;
  }

  const isCheckedIn = status?.is_checked_in;

  return (
    <>
      <div className="flex items-center gap-2">
        {isCheckedIn ? (
          <>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <Clock className="h-3 w-3 mr-1" />
              In: {formatTime(status?.active_record?.check_in_time)}
            </Badge>
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => handleAction('check-out')}
            >
              <LogOut className="h-4 w-4 mr-1" />
              Check Out
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="default"
            className="bg-green-600 hover:bg-green-700"
            onClick={() => handleAction('check-in')}
          >
            <LogIn className="h-4 w-4 mr-1" />
            Check In
          </Button>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === 'check-in' ? (
                <>
                  <LogIn className="h-5 w-5 text-green-600" />
                  Confirm Check-In
                </>
              ) : (
                <>
                  <LogOut className="h-5 w-5 text-red-600" />
                  Confirm Check-Out
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Your location will be recorded for attendance tracking.
            </DialogDescription>
          </DialogHeader>

          {locationError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Location Required</p>
                  <p className="text-sm text-red-600 mt-1">{locationError}</p>
                  <p className="text-xs text-red-500 mt-2">
                    Please enable location services and try again.
                  </p>
                </div>
              </div>
              <Button
                className="mt-3 w-full"
                variant="outline"
                onClick={() => handleAction(actionType)}
              >
                Retry
              </Button>
            </div>
          ) : currentLocation ? (
            <div className="space-y-4">
              <div className="bg-gray-50 border rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">Location Captured</span>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  <p>Latitude: {currentLocation.latitude.toFixed(6)}</p>
                  <p>Longitude: {currentLocation.longitude.toFixed(6)}</p>
                  {currentLocation.accuracy && (
                    <p>Accuracy: ~{Math.round(currentLocation.accuracy)}m</p>
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <p><strong>Employee:</strong> {user?.name}</p>
                <p><strong>Time:</strong> {new Date().toLocaleTimeString()}</p>
                <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Getting location...</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmAction}
              disabled={!currentLocation || actionLoading}
              className={actionType === 'check-in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {actionLoading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </span>
              ) : (
                `Confirm ${actionType === 'check-in' ? 'Check-In' : 'Check-Out'}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CheckInButton;
